import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, OnInit, output, signal, Signal, Type } from '@angular/core';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { IMDMenuOption, MDContentBoxComponent, MDHeaderComponent, MDIconComponent, MDTagComponent } from '@melodic-ui/components';
import { IEnrollmentDetailItem } from '../../api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { IUserSummary } from '../../api/mylb/interfaces/iuser-summary.interface';
import { GradeStateService } from '@lb/state/grade-state.service';
import { GradingTemplateStateService } from '@lb/state/grading-template-state.service';
import { UserStateService } from '@lb/state/user-state.service';
import { RouterModule } from '@angular/router';
import { NoInstructorComponent } from '../no-instructor/no-instructor.component';
import { format } from 'date-fns';
import { buildLessonTimeSlots, ILessonTimeSlot } from '../lessons/utils/lesson-time-slots.util';
import { GlobalTimezoneService } from '../../services/global-timezone.service';
import { EnrollmentStatus } from '../../api/mylb/enums/enrollment-status.enum';
import { ProgramCompletionSummaryComponent } from '../program-completion-summary/program-completion-summary.component';
import { CopyToClipboardComponent } from '../copy-to-clipboard/copy-to-clipboard.component';
import { EnrollmentTagComponent } from '../enrollment-tag/enrollment-tag.component';
import { ProgramTypeTagComponent } from '../program-type-tag/program-type-tag.component';
import { GradeTagComponent } from '../grade-tag/grade-tag.component';
import { IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { ExternalLinksComponent } from '../external-links/external-links.component';
import { TimezoneStateService } from '@lb/state/timezone-state.service';
import { AccountStateService } from '@lb/state/account-state.service';
import { EnrollmentMemberService } from '@lb/api/mylb/services/enrollment-member.service';
import { IEnrollmentMember } from '../../api/mylb/interfaces/enrollment/ienrollment-member.interface';
import { ProgramType } from '../../api/mylb/enums/program-type.enum';
import { take } from 'rxjs';

@Component({
	selector: 'lb-enrollment-card',
	standalone: true,
	imports: [
		MDContentBoxComponent,
		UserIconComponent,
		MDIconComponent,
		MDHeaderComponent,
		CommonModule,
		RouterModule,
		MDTagComponent,
		NoInstructorComponent,
		ProgramCompletionSummaryComponent,
		CopyToClipboardComponent,
		EnrollmentTagComponent,
		ExternalLinksComponent,
		ProgramTypeTagComponent,
		GradeTagComponent
	],
	templateUrl: './enrollment-card.component.html',
	styleUrl: './enrollment-card.component.scss'
})
export class EnrollmentCardComponent implements OnInit {
	private _timezoneStateService = inject(TimezoneStateService);
	private _accountStateService = inject(AccountStateService);
	private _userStateService = inject(UserStateService);
	private gradeState = inject(GradeStateService);
	private templateState = inject(GradingTemplateStateService);
	private _globalTimezoneService = inject(GlobalTimezoneService);
	private _enrollmentMemberService = inject(EnrollmentMemberService);
	private _availableTimezones = this._timezoneStateService.timezones;

	enrollment = input.required<IEnrollmentDetailItem>();
	userView = input.required<'learner' | 'instructor'>();
	isReadOnly = input<boolean>(false);

	enrollmentGradeDialog = input<Type<Component> | undefined>();
	extraLessonDialog = input<Type<Component> | undefined>();
	enrollmentChangeDialog = input<Type<Component> | undefined>();

	enrollmentAction = output<string>();
	viewDetails = output<number>();

	private adminUrl = signal<string>('');

	public canViewNotes = computed(() => this._accountStateService.isAdmin || this._accountStateService.isInstructor);
	public isUserAdmin = this._accountStateService.isAdmin;
	public isUserInstructor = this._accountStateService.isInstructor;

	// --- Chirp room member stack (T7) ---
	// Replaces the placeholder "learner" identity with the room's real members, for viewers the
	// roster endpoint authorizes: admins, the room's instructor, and its institution's ambassadors.
	// Learners/guardians are never authorized, so member-facing cards keep the placeholder view and
	// a member can never see co-members (OQ15). The client gate below only avoids a guaranteed-403
	// call — the server is the enforcement.
	private readonly _mayViewRoster = computed(
		() =>
			this._accountStateService.isAdmin() ||
			this._accountStateService.isInstructor() ||
			this._accountStateService.userEntitlements().includes('PartnerContact:App:Access')
	);

	isChirpRoom = computed(() => this.enrollment().programTypeID === ProgramType.ChirpRoom);

	roomMembers = signal<IEnrollmentMember[]>([]);
	activeRoomMembers = computed(() => this.roomMembers().filter((member) => member.isActive));
	stackedMembers = computed(() => this.activeRoomMembers().slice(0, 5));
	stackOverflowCount = computed(() => Math.max(0, this.activeRoomMembers().length - 5));
	showMemberStack = computed(() => this.activeRoomMembers().length > 0);

	// User display data is owned by User.Api (never the enrollment model) — resolved from cached summaries.
	learner: Signal<IUserSummary | null> = computed(() => this._userStateService.getUserSummary(this.enrollment().learnerUserID)());
	/** The learner's public tags (type IsAdminOnly = 0), off the same cached summary as the name. Under 13 today. */
	learnerTags = computed(() => this.learner()?.tags ?? []);
	instructor: Signal<IUserSummary | null> = computed(() => {
		const instructorID = this.enrollment().instructorUserID;
		return instructorID ? this._userStateService.getUserSummary(instructorID)() : null;
	});

	isAwaitingSchedule = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.AwaitingSchedule);
	isAwaitingMatch = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.AwaitingMatch);
	isReadyToStart = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.ReadyToStart);
	isPaused = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.Paused);
	isInProgress = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.InProgress);
	isMIA = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.MIA);
	isOnLeaveOfAbsence = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.OnLeaveOfAbsence);

	showLastLessonDate = computed(() => !this.isAwaitingSchedule() && !this.isAwaitingMatch() && !this.isMIA() && !this.isOnLeaveOfAbsence());
	showStartDate = computed(() => !this.isMIA() && !this.isOnLeaveOfAbsence());
	showLessonFrequency = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch());
	showMustStartBy = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch() || this.isReadyToStart());
	showEnrollmentExpiration = computed(() => !this.isAwaitingSchedule() && !this.isAwaitingMatch() && !this.isReadyToStart());
	languageName = computed(() => this.enrollment().languageName);
	programName = computed(() => this.enrollment().programName);
	relatedInstitution = computed(() => this.enrollment().institutionName);
	detailsExpanded = computed(() => this._detailsExpanded());
	private readonly _schedulePendingStatuses = new Set([EnrollmentStatus.AwaitingSchedule, EnrollmentStatus.AwaitingMatch, EnrollmentStatus.ReadyToStart]);
	isInProgressOrLater = computed(() => !this._schedulePendingStatuses.has(this.enrollment().enrollmentStatusID ?? 0));

	showLessonTimes = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch());
	startDateToBeConfirmed = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch());

	private _detailsExpanded = signal<boolean>(true);

	toggleDetails(): void {
		this._detailsExpanded.update((expanded) => !expanded);
	}

	learningNeeds = computed(() => this.enrollment().notes?.filter((x) => x.noteTypeID == 2)[0]);
	languageExperience = computed(() => this.enrollment().notes?.filter((x) => x.noteTypeID == 1)[0]);

	/** Program completion summary is computed server-side and carried on the detail item. */
	programSummary = computed(() => this.enrollment().programSummary);

	/**
	 * Submitted semester final grades for display, one per graded semester. Empty unless the enrollment
	 * has a grading template and at least one resolvable final grade. The effective grade is the override
	 * when present, otherwise the calculated grade; names are resolved from the grade scale (User-agnostic).
	 */
	finalGrades = computed(() => {
		if (!this.enrollment().gradingTemplateID || !this.enrollment().isGraded) {
			return [];
		}

		const template = this.templateState.getTemplateSnapshot(this.enrollment().gradingTemplateID);
		const gradeScale = template ? this.gradeState.getGradeScaleByID(template.gradeScaleID) : null;
		const grades = gradeScale?.grades ?? [];
		return (this.enrollment().semesters ?? [])
			.map((s) => {
				const fg = s.semesterFinalGrade;
				if (!fg) {
					return null;
				}
				const gradeID = fg.isOverriden ? fg.overridenGradeID : fg.overallCalculatedGradeID;
				const gradeName = grades.find((g) => g.gradeID === gradeID)?.gradeName ?? null;
				return gradeName ? { semesterID: s.semesterID, levelName: s.levelName, gradeName } : null;
			})
			.filter((g): g is { semesterID: number; levelName: string; gradeName: string } => g !== null);
	});

	startDateLabel = computed(() => {
		if (this.isAwaitingSchedule() || this.isAwaitingMatch()) {
			return 'Selected Date';
		}

		return 'First Lesson Date';
	});

	private _startDateRaw = computed(() => {
		const dateVal = this.enrollment().startDate;
		return dateVal ? new Date(dateVal) : new Date();
	});

	startDate = computed(() => {
		if (this.isAwaitingSchedule()) {
			return null;
		}

		return format(this._startDateRaw(), 'd MMM yyyy');
	});

	endDateLabel = computed(() => 'Last Lesson Date');

	endDate = computed(() => {
		if (this.isAwaitingSchedule() || this.isAwaitingMatch()) {
			return 'N/A';
		}

		const dateVal = this.enrollment().endDate;
		if (!dateVal) {
			return 'N/A';
		}

		return format(new Date(dateVal), 'd MMM yyyy');
	});

	timeZoneName: Signal<string> = computed(() => {
		const timeZones = this._availableTimezones();
		const timezoneID = this.learner()?.timezoneID;
		return timeZones.find((tz) => tz.timeZoneID === timezoneID)?.timeZoneName ?? '';
	});

	timeSlotSelections: Signal<ILessonTimeSlot[]> = computed(() =>
		buildLessonTimeSlots({
			schedules: this.enrollment().enrollmentSchedules ?? [],
			availableTimezones: this._availableTimezones(),
			selectedTimezone: this._globalTimezoneService.selectedTimezone(),
			fallbackTimezoneID: this.learner()?.timezoneID
		})
	);

	menuOptions = computed<IMDMenuOption[]>(() => [
		// Not-graded enrollments (chirp rooms) have no semester grades to show.
		...(this.enrollment().isGraded
			? [
					{
						label: "Semester's grades",
						click: () => this.enrollmentAction.emit('semester-grades')
					}
				]
			: []),
		{
			label: 'Propose extra lessons',
			click: () => this.enrollmentAction.emit('extra-lessons')
		},
		{
			label: 'Suggest other changes',
			click: () => this.enrollmentAction.emit('suggest-changes')
		}
	]);

	constructor() {
		this.gradeState.loadGradeScales();

		// User summaries are globally cached and request-deduped, so each card loading its own is cheap.
		effect(() => {
			const enrollment = this.enrollment();
			const userIDs = [enrollment.learnerUserID, enrollment.instructorUserID].filter((id): id is number => id != null);
			if (userIDs.length) {
				this._userStateService.loadUserSummaries(userIDs);
			}

			// Lazy (D3): first card with a template triggers the one fetch-all; template-less
			// enrollments (no grade UI) never do.
			this.templateState.loadGradingTemplates([enrollment.gradingTemplateID]);
		});

		// Roster for the member stack — requested only for chirp rooms, and only by viewers the
		// endpoint will authorize. Errors (incl. an out-of-scope 403) degrade to the placeholder view.
		effect(() => {
			const enrollmentID = this.enrollment().enrollmentID;
			if (!this.isChirpRoom() || !this._mayViewRoster()) return;

			this._enrollmentMemberService
				.getMembers(enrollmentID)
				.pipe(take(1))
				.subscribe({
					next: (members) => this.roomMembers.set(members),
					error: () => this.roomMembers.set([])
				});
		});

		// Member names for the stack tooltips come from cached User.Api summaries (IDs only on the
		// roster) — the relationship graph put these IDs in this viewer's scope.
		effect(() => {
			const memberUserIDs = this.activeRoomMembers().map((member) => member.userID);
			if (memberUserIDs.length) {
				this._userStateService.loadUserSummaries(memberUserIDs);
			}
		});
	}

	memberName(userID: number): string {
		return this._userStateService.getUserSummary(userID)()?.fullName ?? '';
	}

	ngOnInit(): void {
		fetch('assets/app-settings.json')
			.then((response) => response.json())
			.then(async (config: IAppSettings) => {
				if (config.adminAppUrl == null) {
					console.error('Admin app URL is not defined in the configuration.');
				}

				this.adminUrl.set(config.adminAppUrl ?? '');
			});
	}

	openLink(type: 'instructor' | 'enrollment' | 'learner'): void {
		if (!this.isUserAdmin()) {
			return;
		}

		switch (type) {
			case 'instructor':
				window.open(`${this.adminUrl()}/instructor/${this.enrollment().instructorUserID}/basic-information`, '_blank');
				break;
			case 'enrollment':
				window.open(`${this.adminUrl()}/enrollments/${this.enrollment().enrollmentID}`, '_blank');
				break;
			case 'learner':
				window.open(`${this.adminUrl()}/learner/${this.enrollment().learnerUserID}/basic-information`, '_blank');
				break;
			default:
				console.error('Invalid type provided for openLink:', type);
				break;
		}
	}
}
